/**
 * Encryption Configuration (AES-256-CBC)
 * 
 * Centralized configuration for data encryption/decryption
 * Supports environment variables for easy configuration per environment
 * 
 * IMPORTANT: This uses AES-256-CBC encryption (not just Base64 encoding)
 * Matches Laravel backend's DataEncryptionService
 */

const encodingConfig = {
  /**
   * Master switch - Enable/disable encryption globally
   * Environment variable: REACT_APP_ENCODING_ENABLED (true/false)
   * Default: true (but auto-disabled if no encryption key is set)
   * 
   * Note: Set to false only for testing. Production should always use encryption.
   */
  enabled: process.env.REACT_APP_ENCODING_ENABLED !== 'false' && 
           (!!process.env.REACT_APP_ENCRYPTION_KEY || !!process.env.REACT_APP_APP_KEY),

  /**
   * Enable encryption for requests
   * Environment variable: REACT_APP_ENCODE_REQUESTS (true/false)
   * Default: true
   */
  encodeRequests: process.env.REACT_APP_ENCODE_REQUESTS !== 'false',

  /**
   * Enable decryption for responses
   * Environment variable: REACT_APP_DECODE_RESPONSES (true/false)
   * Default: true
   */
  decodeResponses: process.env.REACT_APP_DECODE_RESPONSES !== 'false',

  /**
   * Log encryption/decryption activity (for debugging)
   * Environment variable: REACT_APP_ENCODING_DEBUG (true/false)
   * Default: false
   * 
   * WARNING: Debug mode logs encryption keys and data. Use only in development!
   */
  debug: process.env.REACT_APP_ENCODING_DEBUG === 'true',

  /**
   * Routes/endpoints that should skip encoding
   * Environment variable: REACT_APP_ENCODING_SKIP_ROUTES (comma-separated)
   * Example: /api/upload,/api/upload/*,/api/file/*
   * 
   * Supports exact matches and wildcards:
   * - '/api/upload' - exact match
   * - '/api/upload/*' - wildcard match
   * - '/api/legacy/*' - all legacy endpoints
   */
  skipRoutes: process.env.REACT_APP_ENCODING_SKIP_ROUTES
    ? process.env.REACT_APP_ENCODING_SKIP_ROUTES.split(',').map(r => r.trim())
    : [
        '/api/upload',
        '/api/upload/*',
        '/api/file/*',
        '/api/export/*',
      ],

  /**
   * HTTP methods that should skip encoding
   * Useful if you want to skip encoding for specific HTTP methods
   */
  skipMethods: [
    // 'GET',  // Uncomment to skip GET requests
    // 'DELETE', // Uncomment to skip DELETE requests
  ],

  /**
   * Content types that should skip encoding
   */
  skipContentTypes: [
    'multipart/form-data',
    'application/octet-stream',
  ],
};

/**
 * Check if encryption is enabled globally
 */
export const isEncodingEnabled = () => {
  return encodingConfig.enabled;
};

/**
 * Check if request encryption is enabled
 */
export const isRequestEncodingEnabled = () => {
  return encodingConfig.enabled && encodingConfig.encodeRequests;
};

/**
 * Check if response decryption is enabled
 */
export const isResponseDecodingEnabled = () => {
  return encodingConfig.enabled && encodingConfig.decodeResponses;
};

/**
 * Check if debug logging is enabled
 */
export const isDebugEnabled = () => {
  return encodingConfig.debug;
};

/**
 * Check if a route should skip encoding
 * @param {string} url - The URL to check
 * @returns {boolean}
 */
export const shouldSkipRoute = (url) => {
  if (!url) return false;

  return encodingConfig.skipRoutes.some((route) => {
    // Exact match
    if (route === url) return true;

    // Wildcard match
    if (route.includes('*')) {
      const pattern = route.replace(/\*/g, '.*');
      const regex = new RegExp(`^${pattern}$`);
      return regex.test(url);
    }

    // Check if URL starts with route
    return url.startsWith(route);
  });
};

/**
 * Check if an HTTP method should skip encoding
 * @param {string} method - The HTTP method (GET, POST, etc.)
 * @returns {boolean}
 */
export const shouldSkipMethod = (method) => {
  if (!method) return false;
  return encodingConfig.skipMethods.includes(method.toUpperCase());
};

/**
 * Check if a content type should skip encoding
 * @param {string} contentType - The content type
 * @returns {boolean}
 */
export const shouldSkipContentType = (contentType) => {
  if (!contentType) return false;
  return encodingConfig.skipContentTypes.some((type) =>
    contentType.includes(type)
  );
};

/**
 * Enable encryption globally
 */
export const enableEncoding = () => {
  encodingConfig.enabled = true;
  if (encodingConfig.debug) {
    console.log('✅ Encryption enabled globally');
  }
};

/**
 * Disable encryption globally
 * WARNING: Only use for testing. Production should always use encryption.
 */
export const disableEncoding = () => {
  encodingConfig.enabled = false;
  if (encodingConfig.debug) {
    console.log('❌ Encryption disabled globally');
  }
};

/**
 * Enable debug logging
 */
export const enableDebug = () => {
  encodingConfig.debug = true;
  console.log('🐛 Encoding debug mode enabled');
};

/**
 * Disable debug logging
 */
export const disableDebug = () => {
  encodingConfig.debug = false;
};

/**
 * Add a route to skip list
 * @param {string} route - Route pattern to skip
 */
export const addSkipRoute = (route) => {
  if (!encodingConfig.skipRoutes.includes(route)) {
    encodingConfig.skipRoutes.push(route);
    if (encodingConfig.debug) {
      console.log(`➕ Added skip route: ${route}`);
    }
  }
};

/**
 * Remove a route from skip list
 * @param {string} route - Route pattern to remove
 */
export const removeSkipRoute = (route) => {
  const index = encodingConfig.skipRoutes.indexOf(route);
  if (index > -1) {
    encodingConfig.skipRoutes.splice(index, 1);
    if (encodingConfig.debug) {
      console.log(`➖ Removed skip route: ${route}`);
    }
  }
};

/**
 * Get current configuration
 */
export const getConfig = () => {
  return { ...encodingConfig };
};

/**
 * Update configuration
 * @param {object} newConfig - New configuration to merge
 */
export const updateConfig = (newConfig) => {
  Object.assign(encodingConfig, newConfig);
  if (encodingConfig.debug) {
    console.log('🔧 Configuration updated:', encodingConfig);
  }
};

/**
 * Reset configuration to defaults
 */
export const resetConfig = () => {
  encodingConfig.enabled = true;
  encodingConfig.encodeRequests = true;
  encodingConfig.decodeResponses = true;
  encodingConfig.debug = false;
  encodingConfig.skipRoutes = ['/api/upload', '/api/upload/*', '/api/file/*', '/api/export/*'];
  encodingConfig.skipMethods = [];
  encodingConfig.skipContentTypes = ['multipart/form-data', 'application/octet-stream'];
  
  if (encodingConfig.debug) {
    console.log('🔄 Configuration reset to defaults');
  }
};

// Make configuration available in browser console for debugging
if (typeof window !== 'undefined') {
  window.encodingConfig = {
    enable: enableEncoding,
    disable: disableEncoding,
    enableDebug,
    disableDebug,
    getConfig,
    updateConfig,
    resetConfig,
    addSkipRoute,
    removeSkipRoute,
  };
}

export default encodingConfig;

